import type { Theme, SxProps, Breakpoint } from '@mui/material/styles';

import { useState } from 'react';

import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';

import { _langs } from '_mock';

import { Main } from './main';
import { layoutClasses } from '../classes';
import { NavMobile, NavDesktop } from './nav';
import { navData } from '../config-nav-dashboard';
import { Searchbar } from '../components/searchbar';
import { MenuButton } from '../components/menu-button';
import { LayoutSection } from '../core/layout-section';
import { HeaderSection } from '../core/header-section';
import { AccountPopover } from '../components/account-popover';
import { SettingsPopover } from '../components/settings-popover';
import { LanguagePopover } from '../components/language-popover';
import { NotificationsPopover } from '../components/notifications-popover';
import { SCOPES } from 'common/permissions';

import intl from 'components/i18n/ReactIntlWrapper';
import { ENTITY_TYPE } from 'common/constants';
import { AccountTree, AccountBalance, Badge, Business, Notifications, PersonAddAlt, Receipt, Security, Translate } from '@mui/icons-material';
import Footer from './footer';
import ErrorBoundary from 'components/ErrorBoundary/ErrorBoundary';
import { Backdrop } from '@mui/material';
import Spinner from 'components/Spinner';
import { useLayout } from 'components/Layout/LayoutProvider';
import { useEffect } from 'react';


// ----------------------------------------------------------------------

export type DashboardLayoutProps = {
  sx?: SxProps<Theme>;
  children: React.ReactNode;
  header?: {
    sx?: SxProps<Theme>;
  };
};

export function DashboardLayout({ sx, children, header }: DashboardLayoutProps) {
  const theme = useTheme();

  const [navOpen, setNavOpen] = useState(false);

  const layoutQuery: Breakpoint = 'lg';

  const { loading, setLayoutLoadingMode } = useLayout();

  useEffect(() => {
    setLayoutLoadingMode('layout');
    return () => {
      setLayoutLoadingMode('full-screen');
    };
  }, [setLayoutLoadingMode]);
  
  return (
    <LayoutSection
      /** **************************************
       * Header
       *************************************** */
      headerSection={
        <HeaderSection
          layoutQuery={layoutQuery}
          slotProps={{
            container: {
              maxWidth: false,
              sx: { px: { [layoutQuery]: 2 } },
            },
          }}
          sx={header?.sx}
          slots={{
            leftArea: (
              <>
                <MenuButton
                  onClick={() => setNavOpen(true)}
                  sx={{
                    ml: -1,
                    [theme.breakpoints.up(layoutQuery)]: { display: 'none' },
                  }}
                />
                <NavMobile
                  data={navData}
                  open={navOpen}
                  onClose={() => setNavOpen(false)}
                />
              </>
            ),
            rightArea: (
              <Box gap={1} display="flex" alignItems="center">
                <LanguagePopover data={_langs} />
                <NotificationsPopover/>
                <SettingsPopover
                  data={[
                    {
                      label: intl.get('mainNavigation.setting.users'),
                      href: '/users',
                      icon: <PersonAddAlt />,
                      viewScopes: [SCOPES['users.read']],
                      editScopes: [SCOPES['users.read']],
                      viewRoles: [],
                      editRoles: [],
                      entityType: ENTITY_TYPE.EMPLOYEE
                    },
                    {
                      label: intl.get('mainNavigation.setting.roles'),
                      href: '/roles',
                      icon: <Security />,
                      viewScopes: [SCOPES['users.read']],
                      editScopes: [SCOPES['users.read']],
                      viewRoles: [],
                      editRoles: [],
                      entityType: ENTITY_TYPE.EMPLOYEE
                    },
                    {
                      label: intl.get('mainNavigation.setting.workflows'),
                      href: '/workflows',
                      icon: <AccountTree />,
                      viewScopes: [SCOPES['workflows.read']],
                      editScopes: [SCOPES['workflows.edit']],
                      viewRoles: [],
                      editRoles: [],
                      entityType: ENTITY_TYPE.EMPLOYEE
                    },
                    {
                      label: intl.get('mainNavigation.setting.corporateEntities'),
                      href: '/corporateEntities',
                      icon: <Business />,
                      viewScopes: [SCOPES['corporateentity.add']],
                      editScopes: [SCOPES['corporateentity.edit']],
                      viewRoles: [],
                      editRoles: [],
                      entityType: ENTITY_TYPE.EMPLOYEE
                    },
                    {
                      label: intl.get('notification.configuration.title'),
                      href: '/notifications/configuration',
                      icon: <Notifications />,
                      viewScopes: [SCOPES['notification.config.read']],
                      editScopes: [SCOPES['notification.config.edit']],
                      viewRoles: [],
                      editRoles: [],
                      entityType: ENTITY_TYPE.EMPLOYEE
                    },
                    {
                      label: intl.get('invoiceConfig.title'),
                      href: '/invoice-configuration',
                      icon: <Receipt />,
                      viewScopes: [SCOPES['corporateentity.edit'],SCOPES['taxrate.read'], SCOPES['invoice.type.read']],
                      editScopes: [SCOPES['corporateentity.edit'],SCOPES['taxrate.read'], SCOPES['invoice.type.read']],
                      viewRoles: [],
                      editRoles: [],
                      entityType: ENTITY_TYPE.EMPLOYEE,
                      children: [
                        {
                          label: intl.get('mainNavigation.billing.taxes'),
                          href: '/taxes',
                          icon: <Receipt />,
                          viewScopes: [SCOPES['taxrate.read']],
                          editScopes: [SCOPES['taxrate.read']],
                          viewRoles: [],
                          editRoles: [],
                          entityType: ENTITY_TYPE.EMPLOYEE
                        },
                        {
                          label: intl.get('invoiceTypeManagement.title') || "test",
                          href: '/invoice-types',
                          icon: <Receipt />,
                          viewScopes: [SCOPES['invoice.type.read']],
                          editScopes: [SCOPES['invoice.type.read']],
                          viewRoles: [],
                          editRoles: [],
                          entityType: ENTITY_TYPE.EMPLOYEE
                        },
                      ]
                    },
                    {
                      label: intl.get('notification.configuration.title'),
                      href: '/notifications/configuration',
                      icon: <Notifications />,
                      viewScopes: [SCOPES['notification.config.read']],
                      editScopes: [SCOPES['notification.config.edit']],
                    },
                    {
                      label: intl.get('mainNavigation.setting.translations'),
                      href: '/translations',
                      icon: <Translate />,
                      viewScopes: [SCOPES['locale.read']],
                      editScopes: [SCOPES['locale.edit']],
                      viewRoles: [],
                      editRoles: [],
                      entityType: ENTITY_TYPE.EMPLOYEE
                    },
                    {
                      label: intl.get('mainNavigation.billing.taxes'),
                      href: '/taxes',
                      icon: <Receipt />,
                      viewScopes: [SCOPES['taxrate.read']],
                      editScopes: [SCOPES['taxrate.read']],
                      viewRoles: [],
                      editRoles: [],
                      entityType: ENTITY_TYPE.EMPLOYEE
                    },
                    {
                      label: intl.get('accountManagement.title'),
                      href: '/accounts',
                      icon: <AccountBalance />,
                      viewScopes: [SCOPES['account.read']],
                      editScopes: [SCOPES['account.edit']],
                      viewRoles: [],
                      editRoles: [],
                      entityType: ENTITY_TYPE.CORPORATE_ENTITY
                    },
                    {
                      label: intl.get('mainNavigation.setting.translations'),
                      href: '/translations',
                      icon: <Translate />,
                      viewScopes: [SCOPES['locale.read']],
                      editScopes: [SCOPES['locale.edit']],
                      viewRoles: [],
                      editRoles: [],
                      entityType: ENTITY_TYPE.EMPLOYEE
                    }

                  ]}
                />
                <AccountPopover
                  data={[]}
                />
              </Box>
            ),
          }}
        />
      }
      /** **************************************
       * Sidebar
       *************************************** */
      sidebarSection={
        <NavDesktop data={navData} layoutQuery={layoutQuery} />
      }
      /** **************************************
       * Footer
       *************************************** */
      footerSection={<Footer />}
      /** **************************************
       * Style
       *************************************** */
      cssVars={{
        '--layout-nav-vertical-width': '300px',
        '--layout-dashboard-content-pt': theme.spacing(1),
        '--layout-dashboard-content-pb': theme.spacing(5),
        '--layout-dashboard-content-px': theme.spacing(2),
      }}
      sx={{
        [`& .${layoutClasses.hasSidebar}`]: {
          [theme.breakpoints.up(layoutQuery)]: {
            pl: 'var(--layout-nav-vertical-width)',
          },
        },
        ...sx,
      }}
    >
      <ErrorBoundary>
        <Main>
          <Backdrop
            open={loading} // This should be controlled by a state variable if you want to show/hide the spinner
            sx={(theme) => ({
              zIndex: theme.zIndex.drawer + 99,
              position: 'absolute',
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(220, 220, 220, 0.8)',
            })}
          >
            <Spinner small />      
          </Backdrop>
          {children}
        </Main>
      </ErrorBoundary>
    </LayoutSection>
  );
}
